import React, { ReactNode } from 'react';

import { Achievement, AchievementContext } from '../achievements/types';
import type { GalaxyState } from '../components/SpaceCivMode';
import {
  Position,
  PlayerFacing,
  BeltItem,
  Building,
  BuildingCategory,
  BuildMenuState,
  BiomeConfig,
  ResearchId,
  ItemType,
  BeltType,
} from '../constants';
import type {
  UpgradeAssemblerResult,
  QuickBeltPlacementResult,
  QuickBeltPreviewState,
} from '../GameEngine';
import {
  AssemblerInventoryLayer,
  RejectedItem,
  ChestInventoryLayer,
  PlayerInventory,
  PowerDemandByBuildingType,
  PowerSnapshot,
  SaveData,
  ResourceNodeMenuState,
  MineResourceResult,
  CollectBuildingInventoryResult,
  ItemProductionHistoryEntry,
} from '../GameState';
import { HandCraftingQueueEntry } from '../HandCrafting';
import { PrestigeUpgradeId, PrestigeUpgradeState } from '../PrestigeSystem';
import { ResearchState } from '../ResearchSystem';
import { RendererMode } from '../types/Renderer';

import { MapStateManager } from './MapStateContext';
import { UIStateManager } from './UIStateContext';

// Combined game state from world hook
interface GameWorldState {
  // World state
  isLoading: boolean;
  playerPos: Position;
  gameMap: Record<string, string>;
  buildingLayer: Record<string, string>;
  beltContentLayer: Record<string, BeltItem[]>;
  assemblerInventoryLayer: AssemblerInventoryLayer;
  chestInventoryLayer: ChestInventoryLayer;
  rejectedItemsLayer: Record<string, RejectedItem>;
  minerTimerLayer: Record<string, number>;
  minerOutputBufferLayer: Record<string, number>;
  playerInventory: PlayerInventory;
  prestigePoints: number;
  prestigeCount: number;
  cameraPos: Position;
  currentBiome: string;
  playerFacing: PlayerFacing;
  zoomLevel: number;
  tickSpeedMultiplier: number;
  researchState: ResearchState;
  researchMenuOpen: boolean;
  powerProduction: number;
  powerDemand: number;
  powerSatisfaction: number;
  powerHistory: PowerSnapshot[];
  powerDemandByBuildingType: PowerDemandByBuildingType;
  productionMenuOpen: boolean;
  itemProductionHistory: ItemProductionHistoryEntry[];
  currentTick: number;
  handCraftingQueue: HandCraftingQueueEntry[];
  prestigeMenuOpen: boolean;
  prestigeUpgradeState: PrestigeUpgradeState;
  spaceGalaxyState: GalaxyState;
  rendererMode: RendererMode;
  quickBeltPreview: QuickBeltPreviewState | null;

  // UI menu states from game engine
  helpMenuOpen: boolean;
  saveMenuOpen: boolean;
  leftPanelOpen: boolean;
  debugMenuOpen: boolean;
  achievementMenuOpen: boolean;
  buildingStatusMenuTarget: { x: number; y: number } | null;
  resourceNodeMenu: ResourceNodeMenuState | null;
  hasEnabledDebug: boolean;
  achievements: Achievement[];

  // World actions
  getTile: (
    x: number,
    y: number,
    gameMap: Record<string, string>,
    buildingLayer?: Record<string, string>
  ) => string | undefined;
  getTargetPosition: (building?: Building) => Position;
  getBuildingTargetLocation: (building: Building) => Position;
  placeBuilding: (x: number, y: number, building: Building) => boolean;
  deconstructBuilding: (x: number, y: number) => boolean;
  upgradeAssemblerToFactory: (position: Position) => UpgradeAssemblerResult;
  addItemToInventory: (itemType: ItemType, amount: number) => number;
  queueHandCrafting: (itemType: ItemType, amount: number) => boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setTickSpeedMultiplier: (multiplier: number) => void;
  cycleTickSpeedMultiplier: () => number;
  saveGame: () => boolean;
  loadGameFromSaveData: (saveData: SaveData) => void;
  resetGame: () => void;
  collectResource: (x: number, y: number) => void;
  collectBuildingInventory: (
    position: Position,
    itemType?: ItemType
  ) => CollectBuildingInventoryResult;
  openResourceNodeMenu: (position: Position) => void;
  closeResourceNodeMenu: () => void;
  mineResourceNode: (position: Position) => MineResourceResult;
  updateBiome: (biomeName: string) => void;
  movePlayer: (newX: number, newY: number) => void;
  updateTile: (x: number, y: number, character: string) => void;
  launchRocket: () => void;
  forcePrestige: (count: number) => void;
  startQuickBeltPlacement: (beltType: BeltType, position: Position) => void;
  extendQuickBeltPlacement: (position: Position) => void;
  finalizeQuickBeltPreview: () => void;
  cancelQuickBeltPlacement: () => void;
  confirmQuickBeltPlacement: () => QuickBeltPlacementResult;
  updatePlayerInventory: (
    updater: (inventory: PlayerInventory) => PlayerInventory
  ) => void;
  setHasEnabledDebug: (enabled: boolean) => void;
  setAssemblerInventoryLayer: React.Dispatch<
    React.SetStateAction<AssemblerInventoryLayer>
  >;
  setChestInventoryLayer: React.Dispatch<
    React.SetStateAction<ChestInventoryLayer>
  >;
  setBeltContentLayer: React.Dispatch<
    React.SetStateAction<Record<string, BeltItem[]>>
  >;
  setRejectedItemsLayer: React.Dispatch<
    React.SetStateAction<Record<string, RejectedItem>>
  >;
  setMinerTimerLayer: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setMinerOutputBufferLayer: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setBuildingLayer: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setSpaceGalaxyState: React.Dispatch<React.SetStateAction<GalaxyState>>;
  preGenerateArea: (centerX: number, centerY: number, radius?: number) => void;
  getBiome: (x: number, y: number) => BiomeConfig;
  getAchievements: () => Achievement[];
  triggerAchievementCheck: (context: AchievementContext) => void;
  setBuildingStatusMenuTarget: (
    target: { x: number; y: number } | null
  ) => void;
  openBuildingStatusMenu: (position: { x: number; y: number }) => void;
  closeBuildingStatusMenu: () => void;
  unlockResearch: (researchId: ResearchId) => void;
  forceUnlockResearch: (researchId: ResearchId) => void;
  isResearchUnlocked: (researchId: ResearchId) => boolean;
  getUnlockedResearchIds: () => ResearchId[];
  selectResearch: (researchId: ResearchId | null) => void;
  setResearchMenuOpen: (open: boolean) => void;
  setProductionMenuOpen: (open: boolean) => void;
  setPrestigeMenuOpen: (open: boolean) => void;
  selectPrestigeUpgrade: (upgradeId: PrestigeUpgradeId | null) => void;
  purchasePrestigeUpgrade: (upgradeId: PrestigeUpgradeId) => boolean;
  resetResearch: (researchId: ResearchId) => void;
  resetAllResearch: () => void;
  setRendererMode: (mode: RendererMode) => void;
  toggleRendererMode: () => void;
}

// GameStateManager Props
interface GameStateManagerProps {
  children: ReactNode;
  worldState: GameWorldState;
  buildingState: {
    buildMode: boolean; // Derived from GameEngine selectedBuilding !== null
    selectedCategory: BuildingCategory | null;
    selectedBuilding: Building | null;
    buildMenuOpen: BuildMenuState;
    setSelectedCategory: (category: BuildingCategory | null) => void;
    setSelectedBuilding: (building: Building | null) => void;
    setBuildMenuOpen: (open: boolean) => void;
    setBuildMenuState: (updates: {
      buildMenuOpen?: BuildMenuState;
      selectedCategory?: BuildingCategory | null;
      selectedBuilding?: Building | null;
    }) => void;
    exitBuildMode: () => void;
  };
}

export const GameStateManager: React.FC<GameStateManagerProps> = ({
  children,
  worldState,
  buildingState,
}) => {
  // Separate world state and actions
  const mapState = {
    isLoading: worldState.isLoading,
    playerPos: worldState.playerPos,
    gameMap: worldState.gameMap,
    buildingLayer: worldState.buildingLayer,
    beltContentLayer: worldState.beltContentLayer,
    assemblerInventoryLayer: worldState.assemblerInventoryLayer,
    chestInventoryLayer: worldState.chestInventoryLayer,
    rejectedItemsLayer: worldState.rejectedItemsLayer,
    minerTimerLayer: worldState.minerTimerLayer,
    minerOutputBufferLayer: worldState.minerOutputBufferLayer,
    playerInventory: worldState.playerInventory,
    prestigePoints: worldState.prestigePoints,
    prestigeCount: worldState.prestigeCount,
    cameraPos: worldState.cameraPos,
    currentBiome: worldState.currentBiome,
    playerFacing: worldState.playerFacing,
    zoomLevel: worldState.zoomLevel,
    tickSpeedMultiplier: worldState.tickSpeedMultiplier,
    hasEnabledDebug: worldState.hasEnabledDebug,
    achievements: worldState.achievements,
    researchState: worldState.researchState,
    researchMenuOpen: worldState.researchMenuOpen,
    productionMenuOpen: worldState.productionMenuOpen,
    powerProduction: worldState.powerProduction,
    powerDemand: worldState.powerDemand,
    powerSatisfaction: worldState.powerSatisfaction,
    powerHistory: worldState.powerHistory,
    powerDemandByBuildingType: worldState.powerDemandByBuildingType,
    itemProductionHistory: worldState.itemProductionHistory,
    currentTick: worldState.currentTick,
    handCraftingQueue: worldState.handCraftingQueue,
    prestigeMenuOpen: worldState.prestigeMenuOpen,
    prestigeUpgradeState: worldState.prestigeUpgradeState,
    spaceGalaxyState: worldState.spaceGalaxyState,
    resourceNodeMenu: worldState.resourceNodeMenu,
    rendererMode: worldState.rendererMode,
    quickBeltPreview: worldState.quickBeltPreview,
  };

  const mapActions = {
    getTile: worldState.getTile,
    getTargetPosition: worldState.getTargetPosition,
    getBuildingTargetLocation: worldState.getBuildingTargetLocation,
    placeBuilding: worldState.placeBuilding,
    deconstructBuilding: worldState.deconstructBuilding,
    upgradeAssemblerToFactory: worldState.upgradeAssemblerToFactory,
    addItemToInventory: worldState.addItemToInventory,
    queueHandCrafting: worldState.queueHandCrafting,
    zoomIn: worldState.zoomIn,
    zoomOut: worldState.zoomOut,
    resetZoom: worldState.resetZoom,
    setTickSpeedMultiplier: worldState.setTickSpeedMultiplier,
    cycleTickSpeedMultiplier: worldState.cycleTickSpeedMultiplier,
    saveGame: worldState.saveGame,
    loadGameFromSaveData: worldState.loadGameFromSaveData,
    resetGame: worldState.resetGame,
    collectResource: worldState.collectResource,
    collectBuildingInventory: worldState.collectBuildingInventory,
    openResourceNodeMenu: worldState.openResourceNodeMenu,
    closeResourceNodeMenu: worldState.closeResourceNodeMenu,
    mineResourceNode: worldState.mineResourceNode,
    updateBiome: worldState.updateBiome,
    movePlayer: worldState.movePlayer,
    launchRocket: worldState.launchRocket,
    forcePrestige: worldState.forcePrestige,
    startQuickBeltPlacement: worldState.startQuickBeltPlacement,
    extendQuickBeltPlacement: worldState.extendQuickBeltPlacement,
    finalizeQuickBeltPreview: worldState.finalizeQuickBeltPreview,
    cancelQuickBeltPlacement: worldState.cancelQuickBeltPlacement,
    confirmQuickBeltPlacement: worldState.confirmQuickBeltPlacement,
    updatePlayerInventory: worldState.updatePlayerInventory,
    updateTile: worldState.updateTile,
    setHasEnabledDebug: worldState.setHasEnabledDebug,
    setAssemblerInventoryLayer: worldState.setAssemblerInventoryLayer,
    setChestInventoryLayer: worldState.setChestInventoryLayer,
    setBeltContentLayer: worldState.setBeltContentLayer,
    setRejectedItemsLayer: worldState.setRejectedItemsLayer,
    setMinerTimerLayer: worldState.setMinerTimerLayer,
    setMinerOutputBufferLayer: worldState.setMinerOutputBufferLayer,
    setBuildingLayer: worldState.setBuildingLayer,
    setSpaceGalaxyState: worldState.setSpaceGalaxyState,
    preGenerateArea: worldState.preGenerateArea,
    getBiome: worldState.getBiome,
    getAchievements: worldState.getAchievements,
    triggerAchievementCheck: worldState.triggerAchievementCheck,
    unlockResearch: worldState.unlockResearch,
    forceUnlockResearch: worldState.forceUnlockResearch,
    isResearchUnlocked: worldState.isResearchUnlocked,
    getUnlockedResearchIds: worldState.getUnlockedResearchIds,
    selectResearch: worldState.selectResearch,
    setResearchMenuOpen: worldState.setResearchMenuOpen,
    setProductionMenuOpen: worldState.setProductionMenuOpen,
    setPrestigeMenuOpen: worldState.setPrestigeMenuOpen,
    selectPrestigeUpgrade: worldState.selectPrestigeUpgrade,
    purchasePrestigeUpgrade: worldState.purchasePrestigeUpgrade,
    resetResearch: worldState.resetResearch,
    resetAllResearch: worldState.resetAllResearch,
    setRendererMode: worldState.setRendererMode,
    toggleRendererMode: worldState.toggleRendererMode,
  };

  // Extract UI state for UIStateManager
  const gameEngineUIState = {
    helpMenuOpen: worldState.helpMenuOpen,
    saveMenuOpen: worldState.saveMenuOpen,
    leftPanelOpen: worldState.leftPanelOpen,
    debugMenuOpen: worldState.debugMenuOpen,
    achievementMenuOpen: worldState.achievementMenuOpen,
    researchMenuOpen: worldState.researchMenuOpen,
    prestigeMenuOpen: worldState.prestigeMenuOpen,
    productionMenuOpen: worldState.productionMenuOpen,
    buildingStatusMenuTarget: worldState.buildingStatusMenuTarget,
  };

  // Extract GameEngine actions for UIStateManager
  const gameEngineActions = {
    openBuildingStatusMenu: worldState.openBuildingStatusMenu,
    closeBuildingStatusMenu: worldState.closeBuildingStatusMenu,
    setBuildingStatusMenuTarget: worldState.setBuildingStatusMenuTarget,
    setResearchMenuOpen: worldState.setResearchMenuOpen,
    setPrestigeMenuOpen: worldState.setPrestigeMenuOpen,
    setProductionMenuOpen: worldState.setProductionMenuOpen,
  };

  return (
    <UIStateManager
      buildingState={buildingState}
      gameEngineUIState={gameEngineUIState}
      gameEngineActions={gameEngineActions}
    >
      <MapStateManager worldState={mapState} worldActions={mapActions}>
        {children}
      </MapStateManager>
    </UIStateManager>
  );
};

// Export hooks for easy access
export { useUIState } from './UIStateContext';
export { useMapState } from './MapStateContext';
